import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp, orderBy } from 'firebase/firestore';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function ScheduleTab({ user, centerData }) {
  const [jobs, setJobs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => { if (centerData?.id) loadJobs(); else setLoading(false); }, [centerData, currentDate]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const end   = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      const q = query(
        collection(db, 'washRequests'),
        where('serviceCenterId', '==', centerData.id),
        where('scheduledAt', '>=', Timestamp.fromDate(start)),
        where('scheduledAt', '<=', Timestamp.fromDate(end)),
        orderBy('scheduledAt', 'asc')
      );
      const snap = await getDocs(q);
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const getDaysInMonth = () => {
    const year  = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const getJobsForDay = (day) => {
    if (!day) return [];
    return jobs.filter(j => {
      const d = j.scheduledAt?.toDate?.();
      return d && d.getDate() === day && d.getMonth() === currentDate.getMonth();
    });
  };

  const today = new Date();
  const isToday = (day) => day && today.getDate() === day && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>Schedule</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={prevMonth} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>←</button>
          <span style={{ fontSize: 16, fontWeight: 800, minWidth: 180, textAlign: 'center' }}>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
          <button onClick={nextMonth} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>→</button>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 8 }}>
          {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#6b7280', padding: '8px 0', textTransform: 'uppercase' }}>{d}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {getDaysInMonth().map((day, i) => {
            const dayJobs = getJobsForDay(day);
            return (
              <div key={i} style={{ minHeight: 80, padding: 6, borderRadius: 8, background: isToday(day) ? '#fff7ed' : day ? '#fafafa' : 'transparent', border: isToday(day) ? '2px solid #F97316' : '1px solid #f3f4f6' }}>
                {day && (
                  <>
                    <div style={{ fontSize: 13, fontWeight: isToday(day) ? 900 : 600, color: isToday(day) ? '#F97316' : '#374151', marginBottom: 4 }}>{day}</div>
                    {dayJobs.slice(0, 2).map(job => (
                      <div key={job.id} style={{ fontSize: 10, background: '#F97316', color: '#fff', borderRadius: 4, padding: '2px 4px', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.scheduledAt?.toDate?.()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} {(job.selectedServices || [])[0] || 'Service'}
                      </div>
                    ))}
                    {dayJobs.length > 2 && <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>+{dayJobs.length - 2} more</div>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Job list for month */}
      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>All Jobs This Month ({jobs.length})</h3>
        {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : jobs.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, textAlign: 'center', color: '#6b7280' }}>No jobs scheduled this month</div>
        ) : jobs.map(job => (
          <div key={job.id} style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{(job.selectedServices || []).join(', ') || 'Service'}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{job.scheduledAt?.toDate?.()?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: job.centerStatus === 'completed' ? '#f0fdf4' : job.centerStatus === 'in_service' ? '#eff6ff' : '#fff7ed', color: job.centerStatus === 'completed' ? '#16a34a' : job.centerStatus === 'in_service' ? '#3b82f6' : '#F97316' }}>
              {job.centerStatus || 'scheduled'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}